import { useTranslation } from 'react-i18next';

import { formatHoursToTimeString } from '@/helpers';
import { TimeOffRequest } from '@/modules/time-off/types/time-off-request';

import { findTimeOffRequestsForDate } from '../../helpers/time-off-request-match.helper';
import { byTimeOffRequestStatus } from '../../helpers/time-off-sort.helper';
import { TimeOffSummaryItem } from '../../types/time-tracking';
import { TimeOffDetailsCard } from '../time-off/time-off-details-card';

interface DayTimeOffCardProps {
  /** Per-status time-off entries for the selected day (drives the header hours). */
  entries: TimeOffSummaryItem[];
  /** Full time-off requests for the period (filtered here to the selected day). */
  requests: TimeOffRequest[];
  /** Raw daily capacity (8h on a weekday) — shown in the header subtitle. */
  totalCapacityHours: number;
  /** The day currently shown — used to pick this day's requests + part-of-day. */
  date: Date;
}

/**
 * Day-view companion card listing every time-off request that covers the
 * selected day. Each entry renders the shared `TimeOffDetailsCard`, separating the
 * request's general info (type, range, approvers, description) from this day's
 * part-of-day. Returns `null` when the day has no time-off.
 */
export function DayTimeOffCard({
  entries,
  requests,
  totalCapacityHours,
  date,
}: DayTimeOffCardProps) {
  const { t } = useTranslation('authenticated.timeTracking');
  if (entries.length === 0) return null;

  const totalTimeOff = entries.reduce((sum, e) => sum + e.totalHours, 0);

  // Requests covering this day, sorted PENDING first (top) — matches week/month.
  const matches = findTimeOffRequestsForDate(requests, date).sort(
    byTimeOffRequestStatus(match => match.request.status)
  );

  return (
    <div className="rounded-lg border bg-card shadow-sm">
      <div className="flex flex-col gap-4 p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">{t('dayView.timeOffCard.title')}</h2>
          <span className="text-[12px] text-muted-foreground">
            {t('dayView.timeOffCard.subtitle', {
              hours: formatHoursToTimeString(totalTimeOff),
              capacity: formatHoursToTimeString(totalCapacityHours),
            })}
          </span>
        </div>

        <div className="flex flex-col gap-2">
          {matches.map(({ request, day }) => (
            <TimeOffDetailsCard
              key={request.id}
              request={request}
              dayLines={[day]}
              dayLinesLabel={t('timeOff.thisDay')}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
